import React, { useEffect, useMemo, useRef } from "react";

import PropTypes from "prop-types";
import cs from "classnames";

import Swiper from "react-id-swiper";
import SwiperButton from "@lululemon/ecom-common/src/components/widgets/SwiperButton";

import { Icon } from "@lululemon/ui-library";
import { rightChevronIcon, leftChevronIcon } from "@lululemon/ui-icons";

import LinkGenerator from "@lululemon/ecom-common/src/lib/link-generator";
import {
  EVENT_CATEGORY,
  EVENT_TYPE,
  trackEvent,
} from "@lululemon/mwa-analytics";

import { ErrorBoundary } from "@lululemon/mwa-logger";
import { productEdHandleError } from "@lululemon/ecom-common/src/util/product-education-logger";

import styles from "./cdp-hero-navigation.module.scss";

export const CDPHeroNavItem = ({
  callToAction,
  isSelected,
  position,
  totalCount,
  ...rest
}) => {
  /*
  Extra <span> wrappers are required here to avoid
  "Failed to execute 'removeChild' on 'Node'"
  error when using Google Translate
  */
  const linkText = isSelected ? (
    <>
      <span>{callToAction}</span>
      <span className="sr-only">&nbsp;(Current Page)</span>
    </>
  ) : (
    <span>{callToAction}</span>
  );

  return (
    <LinkGenerator
      className={cs("lll-text-body-2", styles.cdpHeroNavigationLink, {
        [styles.cdpHeroNavigationLinkSelected]: isSelected,
      })}
      {...rest}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => {
        trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, {
          eventSubType: EVENT_TYPE.INTERACTION,
          component: {
            id: "hero-nav",
            type: "cdp-hero-navigation",
            text: callToAction,
            position: position,
            totalCount: totalCount,
          },
        });
      }}
    >
      {linkText}
    </LinkGenerator>
  );
};

const CDPHeroNavigation = ({
  currentBreakpoint,
  navigation,
  isMarkdown,
  currentPage,
}) => {
  const swiperRef = useRef(null);

  const isMobile = {
    small: true,
    mobile: true,
    tablet: true,
  }[currentBreakpoint];

  const getActiveSlide = () => {
    const ofActiveSlide = (item) => item.url.includes(currentPage);
    return navigation.findIndex(ofActiveSlide);
  };

  const carouselParams = useMemo(() => {
    const carouselNextIcon = (
      <SwiperButton
        className={styles.cdpHeroNavigationButtonNext}
        swiperID="cdpHeroSlider"
        onMouseDown={(e) => e.preventDefault()}
        buttonType="next"
        title="Next Slide"
        tabIndex="-1"
        role="presentation"
        aria-label=""
        customIcon={() => <Icon content={rightChevronIcon} />}
      />
    );

    const carouselPrevIcon = (
      <SwiperButton
        className={styles.cdpHeroNavigationButtonPrev}
        swiperID="cdpHeroSlider"
        onMouseDown={(e) => e.preventDefault()}
        buttonType="prev"
        title="Previous Slide"
        tabIndex="-1"
        role="presentation"
        aria-label=""
        customIcon={() => <Icon content={leftChevronIcon} />}
      />
    );

    return {
      ref: swiperRef,
      grabCursor: isMobile,
      simulateTouch: isMobile,
      speed: 900,
      slidesPerGroup: 1,
      watchSlidesProgress: true,
      watchSlidesVisibility: true,
      navigation: {
        nextEl: `.${styles.cdpHeroNavigationButtonNext}`,
        prevEl: `.${styles.cdpHeroNavigationButtonPrev}`,
        disabledClass: "swiper-button--disabled",
      },
      renderCustomPrevButton: () => carouselPrevIcon,
      renderCustomNextButton: () => carouselNextIcon,
      slidesPerView: "auto",
      centeredSlides: false,
      initialSlide: getActiveSlide(),
      watchOverflow: true,
      containerClass: cs(styles.swiperContainer, styles.cdpHeroSwiperContainer),
      ContainerEl: "nav",
      WrapperEl: "ul",
      rebuildOnUpdate: true,
    };
  }, [currentBreakpoint]);

  // reset active slide back to the beginning when resizing to different breakpoint
  useEffect(() => {
    if (!swiperRef.current) return;

    const { initialSlide } = carouselParams;

    swiperRef.current.swiper.slideTo(initialSlide);
    swiperRef.current.swiper.update();
  }, [currentBreakpoint]);

  const getNofollow = (url = "") => {
    if (!isMarkdown) return null; // 'We Made Too Much' only
    const currPath = window?.location?.pathname || "";
    if (currPath === url) return null; // self-referencing link
    return "nofollow";
  };

  const handleLinkFocus = (index) => {
    if (!swiperRef.current) return;
    swiperRef.current.swiper.slideTo(index);
  };

  return (
    <ErrorBoundary
      onError={(error) =>
        productEdHandleError(error, "CdpHeroNavigation", "CDP Hero")
      }
    >
      <div
        data-testid="cdp-hero-navigation"
        className={styles.cdpHeroNavigation}
      >
        <Swiper {...carouselParams}>
          {navigation.map(({ text, url, linkAttributes }, index) => (
            <li
              key={`${text.split(" ").join("")}${url}`}
              data-testid="navigation-link"
              className={cs(styles.cdpHeroNavigationItem, styles.swiperSlide)}
            >
              <CDPHeroNavItem
                callToAction={text}
                href={url}
                isSelected={url === currentPage}
                onFocus={() => handleLinkFocus(index)}
                rel={linkAttributes?.rel || getNofollow(url)}
                target={linkAttributes?.openNewWindow ? "_blank" : undefined}
                position={index + 1}
                totalCount={navigation?.length}
              />
            </li>
          ))}
        </Swiper>
      </div>
    </ErrorBoundary>
  );
};

CDPHeroNavItem.defaultProps = {
  isSelected: false,
  rel: "",
  target: undefined,
};

CDPHeroNavItem.propTypes = {
  callToAction: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  rel: PropTypes.string,
  target: PropTypes.string,
  isSelected: PropTypes.bool,
  onFocus: PropTypes.func.isRequired,
  position: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
};

CDPHeroNavigation.defaultProps = {
  isMarkdown: false,
  currentPage: "",
};

CDPHeroNavigation.propTypes = {
  currentBreakpoint: PropTypes.string.isRequired,
  navigation: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      linkAttributes: PropTypes.shape({
        rel: PropTypes.string,
        openNewWindow: PropTypes.bool,
      }),
    })
  ).isRequired,
  isMarkdown: PropTypes.bool,
  currentPage: PropTypes.string,
};

export default CDPHeroNavigation;
